import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { GoalSpinResultModal } from "@/apps/goals/components/GoalSpinResultModal/GoalSpinResultModal";
import { WinnersTicker } from "@/apps/goals/components/WinnersTicker/WinnersTicker";
import { Wheel } from "@/apps/goals/components/Wheel/Wheel";
import { useGoalsStore } from "@/apps/goals/store/goalsStore";
import { GoalTemplate } from "@/apps/goals/types";
import { formatGoalRewardAmount } from "@/apps/goals/utils/formatTime";
import { getGoalsScreenUrl } from "@/apps/goals/utils/goalsRoutes";
import { useStartupParamsStore } from "@/store/startupParams";
import Typography from "@/ui/Typography/Typography";
import cls from "./styles.module.scss";

export const WheelPage = () => {
  const { t } = useTranslation();
  const navigateTimeoutRef = useRef<number>(null);
  const templates = useGoalsStore(
    (state) => state.initData?.program.templates ?? []
  );
  const rewardAmount = useGoalsStore(
    (state) => state.initData?.program.goal_price ?? 0
  );
  const spinGoal = useGoalsStore((state) => state.spinGoal);
  const activateGoal = useGoalsStore((state) => state.activateGoal);
  const yandexId = useStartupParamsStore((state) => state.auth.yandexId);
  const [isGoalModalOpen, setIsGoalModalOpen] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<GoalTemplate | null>(
    null
  );
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(
    () => () => {
      if (navigateTimeoutRef.current) {
        window.clearTimeout(navigateTimeoutRef.current);
      }
    },
    []
  );

  const handleSpin = () => {
    if (!yandexId) {
      return Promise.reject();
    }

    return spinGoal(yandexId);
  };

  const handleSpinComplete = (template: GoalTemplate) => {
    setSelectedTemplate(template);
    setIsGoalModalOpen(true);
  };

  const continueToActiveGoal = () => {
    setIsGoalModalOpen(false);
    setIsLeaving(true);

    navigateTimeoutRef.current = window.setTimeout(() => {
      activateGoal();
      navigate({
        pathname: getGoalsScreenUrl("activeGoal"),
        search: location.search,
      });
    }, 320);
  };

  return (
    <section className={[cls.root, isLeaving ? cls.rootLeaving : ""].join(" ")}>
      <div className={cls.content}>
        <Typography
          className={cls.title}
          component="h1"
          size="3xl"
          weight="bold"
          family="custom"
        >
          {t("goals.wheel.titleStart")}
          <br />
          {t("goals.wheel.titleEnd")}{" "}
          <span className={cls.reward}>
            {t("goals.wheel.reward", {
              price: formatGoalRewardAmount(rewardAmount),
            })}
          </span>
        </Typography>
        <WinnersTicker />
        <Wheel
          templates={templates}
          onSpin={handleSpin}
          onSpinComplete={handleSpinComplete}
        />
      </div>
      <GoalSpinResultModal
        isOpen={isGoalModalOpen}
        rides={selectedTemplate?.rides_required ?? null}
        rewardAmount={selectedTemplate ? rewardAmount : null}
        endsAt={
          selectedTemplate?.current_assignment?.ends_at
            ? new Date(selectedTemplate.current_assignment.ends_at).getTime()
            : undefined
        }
        onConfirm={continueToActiveGoal}
      />
    </section>
  );
};
